import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { HourglassSpinnerWrapper } from "../components/HourglassSpinnerWrapper";
import { KairosLandingPage } from "../components/KairosLandingPage";
import { AuthModal } from "../components/AuthModal";
import { SupportRequestModal } from "../components/SupportRequestModal";
import { VersionDisplay } from "@cognixis-technology/common-react-components";
import { getAppVersionLabel } from "../config/build-version-label";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import "./ReturnToMainSiteButton.css";

export const AuthLandingPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const { isAuthenticated, loading, login, register, setExternalAuth } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const rawReturnUrl = searchParams.get("returnUrl");
  const fromExtension = searchParams.get("from") === "extension";
  const shouldRegister =
    searchParams.get("register") === "true" || fromExtension;
  const shouldOpenSupport = searchParams.get("support") === "true";
  const supportAutoOpenRef = React.useRef(false);

  // Compute initialTab directly from URL params (not state) to ensure it's available immediately
  const initialTab: "login" | "register" = shouldRegister
    ? "register"
    : "login";

  // Validate and sanitize returnUrl to prevent XSS attacks
  const returnUrl = React.useMemo(() => {
    if (!rawReturnUrl) {
      return null;
    }

    try {
      // Only allow http/https URLs
      const url = new URL(rawReturnUrl);
      if (url.protocol !== "http:" && url.protocol !== "https:") {
        return null;
      }
      return url.toString();
    } catch (error) {
      return null;
    }
  }, [rawReturnUrl]);

  useEffect(() => {
    if (shouldRegister && !isAuthenticated && !loading) {
      setIsAuthModalOpen(true);
    }
  }, [shouldRegister, isAuthenticated, loading]);

  useEffect(() => {
    if (!shouldOpenSupport) {
      supportAutoOpenRef.current = false;
      return;
    }

    if (supportAutoOpenRef.current || isAuthenticated || loading) {
      return;
    }

    setIsSupportModalOpen(true);
    supportAutoOpenRef.current = true;
  }, [shouldOpenSupport, isAuthenticated, loading]);

  useEffect(() => {}, [returnUrl, rawReturnUrl]);

  // Hide scrollbar on body/html when on login page, but allow scrolling if needed
  useEffect(() => {
    // Add class to body to target with CSS
    document.body.classList.add("login-page");
    document.documentElement.classList.add("login-page");

    return () => {
      // Cleanup: Remove class when component unmounts or user navigates away
      document.body.classList.remove("login-page");
      document.documentElement.classList.remove("login-page");
      // Reset body styles to ensure no lingering effects
      document.body.style.overflow = "";
      document.body.style.height = "";
      document.body.style.width = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
      document.documentElement.style.width = "";
    };
  }, []);

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // CRITICAL: Only redirect if we're actually on the landing/login page
      // This prevents redirect when user has navigated to a protected route before auth completes
      const isLandingPage =
        location.pathname === "/" || location.pathname === "/login";

      if (isLandingPage) {
        // Cleanup login-page class before navigation
        document.body.classList.remove("login-page");
        document.documentElement.classList.remove("login-page");
        document.body.style.overflow = "";
        document.body.style.height = "";
        document.body.style.width = "";
        document.documentElement.style.overflow = "";
        document.documentElement.style.height = "";
        document.documentElement.style.width = "";

        navigate("/dashboard");
      }
    }
  }, [isAuthenticated, navigate, location.pathname]);

  // Show full-screen loader continuously from login button press until dashboard loads:
  // - While auth is being checked (loading)
  // - While login/register is in progress (isLoading)
  // - After auth completes but before navigation finishes (isAuthenticated + still on login page)
  // This ensures NO GAPS in hourglass display
  const isOnLoginPage =
    location.pathname === "/" || location.pathname === "/login";
  const shouldShowHourglass =
    loading || isLoading || (isAuthenticated && isOnLoginPage);

  if (shouldShowHourglass) {
    return (
      <HourglassSpinnerWrapper
        fullPage={true}
        title="Signing you in..."
        subtitle="Please wait while we prepare your dashboard"
      />
    );
  }

  // CRITICAL: If authenticated, return null immediately to prevent any rendering
  // The redirect will happen in AppRoutes, but this prevents any flash
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="auth-landing-root bg-slate-900 relative">
      <div className="auth-landing-scroll">
        <KairosLandingPage
          onLoginClick={() => setIsAuthModalOpen(true)}
          onSupportClick={() => setIsSupportModalOpen(true)}
        />
      </div>

      <footer className="auth-landing-footer-bar" role="contentinfo">
        <p className="auth-landing-footer-copyright">
          Copyright © 2026 Cognixis LLC. All rights reserved.
        </p>
        <div className="auth-landing-footer-version">
          <VersionDisplay version={getAppVersionLabel()} />
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onLogin={login}
        onRegister={register}
        initialTab={initialTab}
        onLoginSuccess={(token, profile) => {
          if (token && profile) {
            setExternalAuth(token, profile);
          }
          setTimeout(() => {
            navigate("/dashboard");
          }, 500);
        }}
        onRegisterSuccess={(token, profile) => {
          // CRITICAL: Set auth state immediately to prevent redirect to login
          if (token && profile) {
            setExternalAuth(token, profile);
          }
          // Navigate to dashboard immediately - no delay needed
          // The auth state is set synchronously, so isAuthenticated will be true
          navigate("/dashboard");
        }}
        onLoadingChange={setIsLoading}
      />

      {/* Support Request Modal */}
      <SupportRequestModal
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
      />
    </div>
  );
};
